import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import StockTransferForm from '@/components/warehouse/StockTransferForm';
import WarehouseService from '@/api/warehouses.service';

interface StockTransferFormData {
  transferType: 'internal' | 'inter_warehouse' | 'replenishment';
  sourceLocationId: string;
  destinationLocationId: string;
  sourceWarehouseId?: string;
  destinationWarehouseId?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduledDate: string;
  notes: string;
  items: any[];
}

const CreateStockTransfer: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (formData: StockTransferFormData) => {
    setIsLoading(true);
    
    try {
      const transferData = {
        transferType: formData.transferType,
        sourceLocationId: formData.sourceLocationId,
        destinationLocationId: formData.destinationLocationId,
        sourceWarehouseId: formData.sourceWarehouseId,
        destinationWarehouseId: formData.destinationWarehouseId,
        priority: formData.priority,
        scheduledDate: formData.scheduledDate,
        notes: formData.notes || undefined,
        items: formData.items
      };

      const response = await WarehouseService.createStockTransfer(transferData);

      if (response.data?.success) {
        toast({
          title: "Success",
          description: "Stock transfer created successfully",
        });
        navigate('/warehouse/transfers');
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to create stock transfer",
        });
      }
    } catch (error: any) {
      console.error('Error creating stock transfer:', error);
      
      let errorMessage = "Failed to create stock transfer";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/warehouse/transfers');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/warehouse/transfers')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Transfers
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create Stock Transfer</h1>
        <p className="text-muted-foreground">
          Create a new stock transfer between locations
        </p>
      </div>

      {/* Form */}
      <StockTransferForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CreateStockTransfer; 